import { TranslateService } from '@ngx-translate/core';

import { LocalizedValue } from '@celum/core/model';
import { isObject } from '@celum/core/util';

export interface InstantTranslateOptions {
  /** Language which should be used for resolving. If not set, the current language of the given TranslateService is used. **/
  displayLanguage?: string;
  /** Language used as last fallback. If not set, the fallback language of the given TranslateService is used. **/
  defaultLanguage?: string;
  /** Only for LocalizedValue: resolve to this specific language - if this is set, it will not fall back to other languages. **/
  targetLanguage?: string;
  /** Locale to use as a fallback before the {@see defaultLanguage} is used. **/
  fallbackLocale?: string;
  /** Specify if resolving should fall back to other languages if there is no value for the wanted one. Default: true **/
  useFallbacks?: boolean;
  /** Interpolation params, only used if the key is passed as a plain string. **/
  interpolationParams?: object;
  /** If true, the first available value is returned if there is no value for the specified language and fallbacks. Default: false **/
  useAnyFallback?: boolean;
}

/**
 * Do an instant translation - this method is synchronous - you are responsible for knowing when the translations were loaded.
 * You can use the currentLocaleStream to find out when languages were loaded
 * @param translate translateService instance to use for translation
 * @param key message-key or localized value
 * @param options options to configure how resolving and fallbacks should be managed
 */
export function instantTranslate(translate: TranslateService, key: string | LocalizedValue, options?: InstantTranslateOptions): string;
/**
 * Do an instant translation - this method is synchronous - you are responsible for knowing when the translations were loaded.
 * You can use the currentLocaleStream to find out when languages were loaded
 * @param translate translateService instance to use for translation
 * @param key message-key or localized value
 * @param displayLanguage current ui-language - if not set, the current language of the given translate service is used
 * @param defaultLanguage default language (used as a fallback) - if not set, the fallback language of the given translate service is used
 * @param targetLanguage translate to specific language - if this is set - it will not fallback to other languages
 * @param useFallbacks Specify if translation should fall back to other languages if there is no translation for wanted one
 * @param interpolationParams which will be used if string is passed as string
 * @param fallbackLocale locale which should be used as fallback before use default one
 * @param useAnyFallback if true, it will return the first available translation if there is no translation for the specified language and fallbacks
 */
export function instantTranslate(
  translate: TranslateService,
  key: string | LocalizedValue,
  displayLanguage?: string,
  defaultLanguage?: string,
  targetLanguage?: string,
  useFallbacks?: boolean,
  interpolationParams?: object,
  fallbackLocale?: string,
  useAnyFallback?: boolean
): string;
export function instantTranslate(
  translate: TranslateService,
  key: string | LocalizedValue,
  displayLanguageOrOptions?: string | InstantTranslateOptions,
  defaultLanguage?: string,
  targetLanguage?: string,
  useFallbacks?: boolean,
  interpolationParams?: object,
  fallbackLocale?: string,
  useAnyFallback?: boolean
): string {
  // Check if the third parameter is an options object
  const options: InstantTranslateOptions = isInstantTranslateOptions(displayLanguageOrOptions)
    ? displayLanguageOrOptions
    : { displayLanguage: displayLanguageOrOptions, defaultLanguage, targetLanguage, useFallbacks, interpolationParams, fallbackLocale, useAnyFallback };

  if (typeof key === 'string') {
    return translate.instant(key, options.interpolationParams);
  }

  return evaluateLocalizedValue(
    key,
    options.targetLanguage,
    options.displayLanguage ?? translate.getCurrentLang(),
    options.defaultLanguage ?? translate.getFallbackLang(),
    options.useFallbacks ?? true,
    options.fallbackLocale,
    options.useAnyFallback ?? false
  );
}

function isInstantTranslateOptions(value: string | InstantTranslateOptions): value is InstantTranslateOptions {
  return isObject(value);
}

export function evaluateLocalizedValue(
  input: LocalizedValue,
  languageParam: string,
  displayLanguage: string,
  defaultLanguage: string,
  useFallbacks = true,
  fallbackLocale?: string,
  useAnyFallback = false
): string {
  if (!input) {
    return undefined;
  }

  if (!useFallbacks) {
    return input[languageParam];
  }

  const fallbackValue = input[languageParam] || input[displayLanguage] || input[fallbackLocale] || input[defaultLanguage];
  if (fallbackValue) {
    return fallbackValue;
  }

  if (useAnyFallback) {
    const firstKey = Object.keys(input)[0];
    return input.en ?? input[firstKey] ?? '';
  }

  return '';
}
